//dependencies
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

//utils
import { makeSearchParamsObjSchema } from "./utils";

/**
 * @param schema - zod schema to validate the query params
 * @returns queryParams - the parsed query params
 * @returns handleQueryChange - function to update the query params
 * @description
 * This hook is used to parse and update the query params in the url.
 * It takes a zod schema as an argument and returns the parsed query params and a function to update the query params.
 * The parsed query params are validated against the zod schema.
 * The function to update the query params takes an object as an argument and updates the query params accordingly.
 * If the value of a key is null, undefined or an empty string, the key is removed from the query params.
 * If the value of a key is not null, undefined or an empty string, the key is added to the query params.
 * IMPORTANT: The query params are updated in the url, you should have a .catch() for each value in the schema that handles default cases since a user can edit the value
 * */

type useSearchQueryParamsProps<T> = {
	schema: T;
};

type QueryChangeOptions = {
	replace?: boolean;
	preventScrollReset?: boolean;
	flushSync?: boolean;
};

const useSearchQueryParams = <T extends z.ZodObject<z.ZodRawShape>>({ schema }: useSearchQueryParamsProps<T>) => {
	type State = z.infer<T>;
	const [queryParams, setQueryParams] = useSearchParams();
	const searchParamsObjSchema = makeSearchParamsObjSchema(schema);
	const result = searchParamsObjSchema.parse(queryParams);

	const handleQueryChange = (
		newParams: Partial<State>,
		options: QueryChangeOptions = { replace: true, preventScrollReset: true }
	) => {
		const newQueryParams = queryParams;
		Object.keys(newParams).forEach((key) => {
			const value = newParams[key];
			if (value === null || value === undefined || value === "" || (Array.isArray(value) && value.length === 0)) {
				newQueryParams.delete(key);
			} else if (Array.isArray(value)) {
				// Convert array to comma-separated string
				newQueryParams.set(key, value.join(","));
			} else {
				newQueryParams.set(key, value);
			}
		});
		// flushSync: react-router 7 wraps these commits in React.startTransition; UI keyed off
		// these params (modals, controlled inputs) can read state before a deferred commit lands.
		// Committing synchronously restores v6 behavior.
		setQueryParams(newQueryParams, { flushSync: true, ...options });
	};

	return {
		queryParams: result,
		handleQueryChange,
	};
};

export default useSearchQueryParams;
